/**
 * Deteccao ativa de perda de conexao durante a captura.
 *
 * O `onError` do SDK da Soniox nao serve como unico sinal: ao desligar o Wi-Fi o
 * WebSocket permanece OPEN por dezenas de segundos (ate o TCP expirar), entao o
 * callback so chega muito depois — e ate la o cronometro segue correndo e o audio
 * e descartado silenciosamente. Estas sondas detectam a queda em segundos.
 */

/** Amostras consecutivas em que o buffer precisa crescer para considerar travado. */
export const STALL_GROWTH_CHECKS = 3;

export type ConnectionVerdict = "healthy" | "offline" | "socket_closed" | "stalled";

/**
 * O buffer do WebSocket crescendo sem parar significa que o audio esta sendo
 * enfileirado mas nao sai pela rede — conexao morta mesmo com o socket OPEN.
 * Cobre o caso "conectado ao Wi-Fi mas sem internet", que `navigator.onLine` nao pega.
 */
export const isSocketStalled = (bufferedAmountSamples: number[]): boolean => {
  if (bufferedAmountSamples.length < STALL_GROWTH_CHECKS + 1) return false;

  const recent = bufferedAmountSamples.slice(-(STALL_GROWTH_CHECKS + 1));
  for (let index = 1; index < recent.length; index += 1) {
    if (recent[index] <= recent[index - 1]) return false;
  }

  return recent[recent.length - 1] > 0;
};

export const evaluateConnection = ({
  isOnline,
  socketReadyState,
  bufferedAmountSamples,
}: {
  isOnline: boolean;
  socketReadyState: number | null;
  bufferedAmountSamples: number[];
}): ConnectionVerdict => {
  if (!isOnline) return "offline";
  // 2 = CLOSING, 3 = CLOSED
  if (socketReadyState === 2 || socketReadyState === 3) return "socket_closed";
  if (isSocketStalled(bufferedAmountSamples)) return "stalled";
  return "healthy";
};
